import React, { useState, useEffect, useCallback, useMemo } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import ClearIcon from "@mui/icons-material/Clear";
import EditNoteIcon from "@mui/icons-material/EditNote";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import { useTheme } from "@emotion/react";
import Loader from "../../../components/Loader/Loader";
import {
  getBrainReviewData,
  getReviewColumns,
  getBrainCompetitorData,
  getSSOTData,
} from "../../../services/api";
import APP_CONSTANTS from "../../../utils/config";

import { updateAlertInfo } from "../../../redux/slice/userSlice";
import { useDispatch } from "react-redux";

import TagInput from "../components/TagInput";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import { brainStyles } from "../../../theme/style";
import ColumnVisibilityControl from "../components/ColumnVisibilityControl";
import {
  setLocalStorageData,
  getLocalStorageData,
  parseTagValue,
} from "../components/reviewColumnUtils";
import { useSelector } from "react-redux";
import {
  updatePageSize,
  updateReviewColumnData,
} from "../../../redux/slice/brainSlice";
import { Select, Skeleton, Tab, Tabs } from "@mui/material";
import { InboxOutlined } from "@mui/icons-material";
import { TableVirtuoso } from "react-virtuoso";
import SKUDetailsDrawer from "../components/SKUDetailsDrawer";

// ----------------------------------------------------------------------
// Memoized Row CELLS
// ----------------------------------------------------------------------
const RowCellsComponent = ({
  row,
  columns,
  theme,
  classes,
  frozenColumns,
  rowBg,
  frozenLeftOffsets,
  skuCount = 0,
  skuList,
  tab,
  handleSkuClick = () => {},
}) => {
  const renderCellValue = (colKey, curr_column) => {
    const cell_value = row[colKey];
    const cellType = curr_column?.type;
    const isFrozen = frozenColumns.has(colKey);
    const stickySx = isFrozen
      ? {
          position: "sticky",
          left: frozenLeftOffsets.get(colKey),
          zIndex: 1,
          backgroundColor: rowBg,
          boxShadow: "2px 0 4px -2px rgba(0,0,0,0.08)",
        }
      : {};
    switch (cellType) {
      case "text":
        return (
          <TableCell
            sx={{
              color: theme.palette.brain.table.row.text,
              fontSize: "0.78rem",
              ...stickySx,
            }}
          >
            {cell_value || "–"}
          </TableCell>
        );

      case "tag":
        const tagValues = parseTagValue(cell_value);
        return (
          <TableCell
            sx={{
              color: theme.palette.brain.table.row.text,
              fontSize: "0.78rem",
            }}
          >
            <TagInput
              value={tagValues}
              disabled={true}
              onChange={() => {}}
              maxVisible={2}
            />
          </TableCell>
        );
      default:
        return (
          <TableCell
            sx={{
              color: theme.palette.brain.table.row.text,
              fontSize: "0.78rem",
              ...stickySx,
            }}
          >
            {cell_value || "–"}
          </TableCell>
        );
    }
  };

  return (
    <>
      {tab === "owns" && (
        <TableCell
          className={classes.baseCell}
          sx={{
            position: "sticky",
            left: 0,
            borderRight: `1px solid ${theme.palette.brain.grey.border}`,
            zIndex: 1,
          }}
        >
          {skuList?.length === 0 ? (
            "-"
          ) : (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <TagInput
                value={skuList}
                disabled={true}
                onChange={() => {}}
                maxVisible={1}
                tagLabel={"All SKUs"}
              />

              <Box>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSkuClick(row.upc12, row.description, row.id);
                  }}
                  size="small"
                >
                  <VisibilityOutlinedIcon
                    sx={{
                      fontSize: "16px",
                      color: theme.palette.secondary.main2,
                    }}
                  />
                </IconButton>
              </Box>
            </Box>
          )}
        </TableCell>
      )}
      {columns.map((col) => renderCellValue(col.key, col))}
    </>
  );
};

const areRowPropsEqual = (prevProps, nextProps) => {
  return (
    prevProps.row === nextProps.row && prevProps.columns === nextProps.columns
  );
};

const MemoizedRowCells = React.memo(RowCellsComponent, areRowPropsEqual);

const REVIEW_FILTER_FIELDS = [
  { key: "brand", label: "Brand" },
  { key: "subBrand", label: "Sub Brand" },
  { key: "variant", label: "Variant" },
  { key: "subSegment", label: "Sub Segment" },
];

const EMPTY_REVIEW_FILTER = {
  brand: null,
  subBrand: null,
  variant: null,
  subSegment: null,
};
const EMPTY_UI_FILTERS = {
  brand: "",
  subBrand: "",
  variant: "",
  subSegment: "",
};

const toApiFilter = (filterState) => {
  if (!filterState) return { ...EMPTY_REVIEW_FILTER };
  const source = filterState.filter ?? filterState;
  return {
    brand: source.brand || null,
    subBrand: source.subBrand || null,
    variant: source.variant || null,
    subSegment: source.subSegment || null,
  };
};

const getFilterOptionsFromResponse = (data) =>
  data?.filter_options ?? data?.filter?.filter_options ?? null;

const toApiSearch = (value) => {
  if (typeof value !== "string") return value ?? null;
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
};

// ----------------------------------------------------------------------
// MAIN PAGE COMPONENT
// ----------------------------------------------------------------------

const DisplayPageSize = ({
  selectedPageSize = 20,
  theme,
  handlePageSizeChange = () => {},
}) => {
  const pageSizeList = useMemo(() => {
    return [20, 50, 100];
  }, []);
  return (
    <Select
      labelId={`page-size`}
      value={selectedPageSize}
      onChange={(e) => handlePageSizeChange(e.target.value)}
      renderValue={(selected) => {
        return (
          <>
            <span
              style={{
                color: theme.palette.brain.table.row.text,
              }}
            >
              {selected}
            </span>
          </>
        );
      }}
      sx={{
        height: "35px",
        fontSize: "0.75rem",
        backgroundColor: theme.palette.brain.table.tableBackground,
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: theme.palette.brain.mis.grey.border1,
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: theme.palette.secondary.main2,
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          border: `1px solid ${theme.palette.secondary.main2}`,
        },
      }}
    >
      {(pageSizeList || []).map((option) => (
        <MenuItem key={option} value={option} sx={{ fontSize: "0.75rem" }}>
          {option}
        </MenuItem>
      ))}
    </Select>
  );
};

export default function SSOT() {
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loadingRows, setLoadingRows] = useState(true);
  const [loadingColumns, setLoadingColumns] = useState(true);
  const [frozenColumns, setFrozenColumns] = useState(() => {
    const freezed_columns = getLocalStorageData(APP_CONSTANTS.FREEZED_COLUMNS);
    return new Set(freezed_columns);
  });
  const [page, setPage] = useState(1);
  const theme = useTheme();
  const dispatch = useDispatch();
  const selectedPageSize = useSelector((state) => state.brain.pageSize);
  const classes = brainStyles(theme);
  const columnData = useSelector((state) => state.brain.columnData);
  const [visibleColumns, setVisibleColumns] = useState(new Set([]));
  const [columnOrder, setColumnOrder] = useState([]);
  const [filters, setFilters] = useState({
    brand: "",
    subBrand: "",
    variant: "",
    subSegment: "",
  });
  const [filterOptions, setfilterOptions] = useState({
    brand: [],
    subBrand: [],
    variant: [],
    subSegment: [],
  });
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState(null);
  const [appliedFilter, setAppliedFilter] = useState(EMPTY_REVIEW_FILTER);
  const [fetchTrigger, setFetchTrigger] = useState(0);
  const [tab, setTab] = useState("owns");
  const [skuDrawer, setSkuDrawer] = useState({
    open: false,
    data: {},
  });
  const [recordCount, setRecordCount] = useState({
    owns: 0,
    competitors: 0,
  });
  const [skuRecords, setSkuRecords] = useState(null);
  const displayedColumns = useMemo(() => {
    const visibleCols = columnOrder
      .filter((key) => visibleColumns.has(key))
      .map((key) => columnData?.columns.find((col) => col.key === key))
      .filter(Boolean);

    const frozen = visibleCols.filter((col) => frozenColumns.has(col.key));
    const unfrozen = visibleCols.filter((col) => !frozenColumns.has(col.key));
    return [
      ...frozen,
      {
        can_validate: false,
        confidence_threshold: 0,
        editors: [],
        info: "MJN Key",
        is_editable_column: false,
        is_nullable: false,
        key: "mjn_key",
        label: "MJN KEY",
        type: "text",
        user_edit_access: false,
      },
      ...unfrozen,
    ];
  }, [columnOrder, visibleColumns, columnData, frozenColumns]);

  const [compVisibleColumns, setCompVisibleColumns] = useState(new Set([]));
  const [compColumnOrder, setCompColumnOrder] = useState([]);

  const compDisplayedColumns = useMemo(() => {
    const visibleCols = compColumnOrder
      .filter((key) => compVisibleColumns.has(key))
      .map((key) =>
        columnData?.competitor_columns.find((col) => col.key === key)
      )
      .filter(Boolean);

    const frozen = visibleCols.filter((col) => frozenColumns.has(col.key));
    const unfrozen = visibleCols.filter((col) => !frozenColumns.has(col.key));
    return [...frozen, ...unfrozen];
  }, [compColumnOrder, compVisibleColumns, columnData, frozenColumns]);

  // dynamically determine the active columns based on the selected tab
  const activeColumns =
    tab === "owns" ? displayedColumns : compDisplayedColumns;

  const frozenLeftOffsets = useMemo(() => {
    const map = new Map();
    let left = tab === "owns" ? 155 : 0;
    activeColumns.forEach((c) => {
      if (frozenColumns.has(c.key)) {
        map.set(c.key, left);
        left += APP_CONSTANTS.SMALL_WIDTH_COLUMNS.includes(c.key)
          ? 50
          : c.key === "description"
          ? 600
          : c.user_edit_access
          ? 300
          : 200;
      }
    });
    return map;
  }, [activeColumns, frozenColumns, tab]);

  const toggleFreeze = (key) => {
    setFrozenColumns((prev) => {
      const n = new Set(prev);
      if (n.has(key)) n.delete(key);
      else n.add(key);

      setLocalStorageData(APP_CONSTANTS.FREEZED_COLUMNS, [...n]);
      return n;
    });
  };

  const localStorageSetting = useCallback(
    (all_columns, key_reference_columns, comp_all_columns) => {
      if (!all_columns?.length || !comp_all_columns?.length) return;
      let visibleColumn_data = getLocalStorageData(
        APP_CONSTANTS.VISIBLE_COLUMN
      );
      let columnOrder = getLocalStorageData(APP_CONSTANTS.COLUMN_ORDER);

      const all_column_list = all_columns.map((c) => c.key);
      const user_editable_columns = all_columns
        .filter((c) => c.user_edit_access === true)
        .map((c) => c.key);

      const isSame =
        all_column_list.length === columnOrder.length &&
        all_column_list.every((item) => columnOrder.includes(item));
      const isIncluded = user_editable_columns.every((item) =>
        visibleColumn_data.includes(item)
      );

      if (
        visibleColumn_data?.length === 0 ||
        columnOrder?.length === 0 ||
        !isSame ||
        !isIncluded
      ) {
        const other_non_editable_columns = all_columns
          .filter(
            (item) =>
              item.is_editable_column === false &&
              !key_reference_columns.includes(item.key)
          )
          .map((item) => item.key);
        const user_non_editable_columns = all_columns
          .filter(
            (item) =>
              item.is_editable_column === true &&
              item.user_edit_access === false
          )
          .map((item) => item.key);

        visibleColumn_data = [
          ...key_reference_columns,
          ...user_editable_columns,
        ];
        columnOrder = [
          ...key_reference_columns,
          ...user_editable_columns,
          ...user_non_editable_columns,
          ...other_non_editable_columns,
        ];
        setLocalStorageData(APP_CONSTANTS.VISIBLE_COLUMN, visibleColumn_data);
        setLocalStorageData(APP_CONSTANTS.COLUMN_ORDER, columnOrder);
      }

      if (visibleColumns?.size === 0 || columnOrder?.length === 0) {
        setVisibleColumns(new Set(visibleColumn_data));
        setColumnOrder(columnOrder);
      }

      // =========================
      // Competitor Columns
      // =========================

      let competitor_visibleColumn = getLocalStorageData(
        APP_CONSTANTS.COMPETITOR_VISIBLE_COLUMN
      );

      let competitor_columnOrder = getLocalStorageData(
        APP_CONSTANTS.COMPETITOR_COLUMN_ORDER
      );

      const comp_all_column_list = comp_all_columns.map((c) => c.key);

      const isSame_comp =
        comp_all_column_list.length === competitor_columnOrder.length &&
        comp_all_column_list.every((item) =>
          competitor_columnOrder.includes(item)
        );

      if (
        competitor_visibleColumn?.length === 0 ||
        competitor_columnOrder?.length === 0 ||
        !isSame_comp
      ) {
        const all_non_editable_columns = comp_all_columns
          .filter((item) => !key_reference_columns.includes(item.key))
          .map((item) => item.key);

        competitor_visibleColumn = [
          ...key_reference_columns,
          ...all_non_editable_columns,
        ];

        competitor_columnOrder = [
          ...key_reference_columns,
          ...all_non_editable_columns,
        ];

        setLocalStorageData(
          APP_CONSTANTS.COMPETITOR_VISIBLE_COLUMN,
          competitor_visibleColumn
        );

        setLocalStorageData(
          APP_CONSTANTS.COMPETITOR_COLUMN_ORDER,
          competitor_columnOrder
        );
      }

      if (compVisibleColumns?.size === 0 || compColumnOrder?.length === 0) {
        setCompVisibleColumns(new Set(competitor_visibleColumn));
        setCompColumnOrder(competitor_columnOrder);
      }

      setLoadingColumns(false);
    },
    [
      visibleColumns?.size,
      columnOrder?.length,
      compVisibleColumns?.size,
      compColumnOrder?.length,
    ]
  );

  const loadColumnData = useCallback(async () => {
    try {
      setLoadingColumns(true);
      const resp = await getReviewColumns();
      const statusCode = resp.data.status.statusCode;
      if (statusCode === "nam-200") {
        const columns = resp.data.data.upc_columns;
        const reference_columns = columns
          .filter((item) => !item.is_editable_column)
          .map((item) => item.key);
        const restricted_columns = columns
          .filter((item) => item.is_editable_column && !item.user_edit_access)
          .map((item) => item.key);
        const editable_columns = columns
          .filter((item) => item.user_edit_access)
          .map((item) => item.key);

        dispatch(
          updateReviewColumnData({
            columns: columns,
            key_reference_columns: APP_CONSTANTS.KEY_REFERENCE_COLUMNS,
            reference_columns: reference_columns,
            tag_columns: APP_CONSTANTS.TAG_COLUMNS,
            restricted_columns: restricted_columns,
            editable_columns: editable_columns,
            sku_columns: resp.data.data.sku_columns,
            competitor_columns: resp.data.data.competitor_columns,
            competitor_reference_columns: resp.data.data.competitor_columns
              .filter((item) => !item.is_editable_column)
              .map((item) => item.key),
          })
        );
      }
    } catch (e) {
      console.error(e);
    }
  }, [dispatch]);

  const loadRowData = useCallback(
    async (
      p = 1,
      page_size = APP_CONSTANTS.BRAIN_PAGE_SIZE,
      filterState = EMPTY_REVIEW_FILTER,
      searchQuery = null,
      current_tab = "owns"
    ) => {
      const apiFilter = toApiFilter(filterState);
      const apiSearch = toApiSearch(searchQuery);
      setLoadingRows(true);
      try {
        const res_api =
          current_tab === "owns"
            ? await getSSOTData(p, page_size, apiFilter, apiSearch)
            : await getBrainCompetitorData(p, page_size, apiFilter, apiSearch);
        // const res = res_api.data.data;
        // const res = current_tab === "owns" ? ssotData : brainCompetitorData;
        const data = res_api.data.data;
        setRows(data.rows);
        setPagination(data.pagination);
        if (current_tab === "owns") {
          setRecordCount(data.record_count);
        }
        setSkuRecords(data?.upc_sku_map);

        const nextFilterOptions = getFilterOptionsFromResponse(data);
        if (nextFilterOptions) {
          setfilterOptions(nextFilterOptions);
        }
      } catch (error) {
        dispatch(
          updateAlertInfo({
            message: "Failed to load SSOT data",
            open: true,
            severity: "error",
          })
        );
      } finally {
        setLoadingRows(false);
      }
    },
    [dispatch]
  );

  useEffect(() => {
    if (columnData?.columns?.length > 0) {
      localStorageSetting(
        columnData.columns,
        columnData.key_reference_columns,
        columnData.competitor_columns
      );
    } else {
      loadColumnData();
    }
  }, [columnData, loadColumnData, localStorageSetting]);

  useEffect(() => {
    if (loadingColumns) return;
    loadRowData(page, selectedPageSize, appliedFilter, appliedSearch, tab);
  }, [page, loadingColumns, fetchTrigger]);

  const handleApplyFilters = () => {
    const apiFilter = toApiFilter(filters);
    const apiSearch = toApiSearch(search);
    setAppliedFilter(apiFilter);
    setAppliedSearch(apiSearch);
    if (page !== 1) {
      setPage(1);
    } else {
      setFetchTrigger((t) => t + 1);
    }
  };

  const handleSearch = () => {
    const apiSearch = toApiSearch(search);
    setAppliedSearch(apiSearch);
    if (page !== 1) {
      setPage(1);
    } else {
      setFetchTrigger((t) => t + 1);
    }
  };

  const handleClearSearch = () => {
    setSearch("");
    setAppliedSearch(null);
    if (page !== 1) {
      setPage(1);
    } else {
      setFetchTrigger((t) => t + 1);
    }
  };

  const handleClearFilters = () => {
    setFilters(EMPTY_UI_FILTERS);
    setSearch("");
    setAppliedFilter(EMPTY_REVIEW_FILTER);
    setAppliedSearch(null);
    if (page !== 1) {
      setPage(1);
    } else {
      setFetchTrigger((t) => t + 1);
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const handlePageSizeChange = (page_size) => {
    dispatch(updatePageSize(page_size));
    loadRowData(1, page_size, appliedFilter, appliedSearch, tab);
  };

  const virtuosoComponents = useMemo(() => {
    return {
      Scroller: React.forwardRef((props, ref) => (
        <TableContainer
          component={Box}
          {...props}
          ref={ref}
          sx={{
            "&::-webkit-scrollbar": { height: 6, width: 6 },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: theme.palette.brain.mis.grey.background1,
              borderRadius: 3,
            },
          }}
        />
      )),
      Table: (props) => (
        <Table
          {...props}
          stickyHeader
          size="small"
          sx={{ borderCollapse: "separate" }}
        />
      ),
      TableHead: React.forwardRef((props, ref) => (
        <TableHead {...props} ref={ref} />
      )),
      TableRow: (props) => {
        const idx = props["data-index"];
        const rowBg =
          idx % 2 === 0
            ? theme.palette.brain.drawer.item_background
            : theme.palette.brain.table.row.background;
        return (
          <TableRow
            {...props}
            sx={{
              backgroundColor: rowBg,
              "& > td": { backgroundColor: rowBg },
              "&:hover": {},
              transition: "background-color 0.12s ease",
            }}
          />
        );
      },
      TableBody: React.forwardRef((props, ref) => (
        <TableBody {...props} ref={ref} />
      )),
      EmptyPlaceholder: () => (
        <Box
          sx={{
            border: `1px dashed ${theme.palette.brain.grey.border}`,
            borderRadius: 1.5,
            py: 6,
            px: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            color: "text.secondary",
            bgcolor: theme.palette.brain.table.background1,
            height: "100%",
            minHeight: "300px",
          }}
        >
          <InboxOutlined
            sx={{ fontSize: 40, color: theme.palette.secondary.main2, mb: 1 }}
          />
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: theme.palette.brain.table.header.text,
            }}
          >
            No Records found
          </Typography>
        </Box>
      ),
    };
  }, [theme]); // only rebuild if the theme changes

  const openSkuForUpc = (upc12, description, row_id) => {
    const sku_rows = skuRecords?.[upc12] || [];
    const sku_columns = columnData.sku_columns;

    setSkuDrawer({
      open: true,
      data: {
        row_id: row_id,
        upc: upc12,
        description: description,
        sku_details: {
          rows: sku_rows,
          columns: sku_columns,
        },
      },
    });
  };

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 1,
        padding: "20px 20px 14px 20px",
      }}
    >
      {/* Header Bar */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 1,
          // py: 1,
          borderRadius: 2,
          paddingBottom: "0px",
          gap: 2,
        }}
      >
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
          }}
        >
          <Typography
            variant="h1"
            sx={{
              fontSize: "1.35rem",
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              gap: 1,
              color: theme.palette.secondary.main2,
            }}
          >
            <EditNoteIcon
              sx={{ color: theme.palette.secondary.main2, fontSize: "30px" }}
            />
            SSOT Preview
          </Typography>

          <Typography
            variant="body2"
            sx={{ color: theme.palette.primary.text2 }}
          >
            Manage and review centralized master records used as the single
            source of truth across the platform.
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          {!loadingColumns &&
            (tab === "owns" ? (
              <ColumnVisibilityControl
                columns={columnData?.columns || []}
                allowedColumns={columnData?.editable_columns}
                keyRefColumns={columnData?.key_reference_columns}
                refColumns={columnData?.reference_columns}
                visible={visibleColumns}
                onChange={setVisibleColumns}
                columnOrder={columnOrder}
                onReorder={setColumnOrder}
                theme={theme}
                loading={loadingRows}
                readOnly={true}
                visibleColumnKey={APP_CONSTANTS.VISIBLE_COLUMN}
                columnOrderKey={APP_CONSTANTS.COLUMN_ORDER}
              />
            ) : (
              <ColumnVisibilityControl
                columns={columnData?.competitor_columns || []}
                allowedColumns={[]}
                keyRefColumns={columnData?.key_reference_columns}
                refColumns={columnData?.competitor_reference_columns}
                visible={compVisibleColumns}
                onChange={setCompVisibleColumns}
                columnOrder={compColumnOrder}
                onReorder={setCompColumnOrder}
                theme={theme}
                loading={loadingRows}
                readOnly={true}
                visibleColumnKey={APP_CONSTANTS.COMPETITOR_VISIBLE_COLUMN}
                columnOrderKey={APP_CONSTANTS.COMPETITOR_COLUMN_ORDER}
              />
            ))}
        </Box>
      </Box>

      {/* Status legend + search */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: 2,
          px: 1,
          flexWrap: "wrap",
          borderBottom: "0.5px solid #ccc",
        }}
      >
        <Tabs
          value={tab}
          onChange={(_, v) => {
            setTab(v);
            loadRowData(1, selectedPageSize, appliedFilter, appliedSearch, v);
          }}
          sx={{
            minHeight: 36,

            "& .MuiTab-root": {
              minHeight: 36,
              textTransform: "none",
              fontSize: "0.78rem",
              fontWeight: 600,
              px: 1.75,
              color: theme.palette.brain.table.row.text,
            },
            "& .MuiTabs-indicator": {
              backgroundColor: theme.palette.secondary.main2,
              height: 2.5,
            },
          }}
        >
          <Tab
            label={`MJN Data (${loadingRows ? "..." : recordCount?.owns || 0})`}
            value="owns"
          />
          <Tab
            label={`Competitors Data (${
              loadingRows ? "..." : recordCount?.competitors || 0
            })`}
            value="competitors"
          />
        </Tabs>
        {loadingRows ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexShrink: 0,
              ml: "auto",
              justifyContent: "end",
              marginBottom: "8px",
            }}
          >
            <Skeleton
              variant="rounded"
              sx={{ minWidth: "280px", height: "30px" }}
            />
            <Skeleton
              variant="rounded"
              sx={{ minWidth: "64px", height: "29px" }}
            />
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexShrink: 0,
              ml: "auto",
              justifyContent: "end",
              marginBottom: "8px",
            }}
          >
            <TextField
              size="small"
              placeholder="Search products..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              InputProps={{
                endAdornment:
                  search.trim() || appliedSearch ? (
                    <InputAdornment position="end">
                      <Tooltip title="Clear search">
                        <IconButton
                          size="small"
                          aria-label="Clear search"
                          onClick={handleClearSearch}
                          onMouseDown={(e) => e.preventDefault()}
                          edge="end"
                          sx={{
                            mr: 0.25,
                            color: theme.palette.secondary.main2,
                            opacity: 0.85,
                            "&:hover": {
                              opacity: 1,
                              backgroundColor: theme.palette.action.hover,
                            },
                          }}
                        >
                          <ClearIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ) : null,
              }}
              sx={{
                width: 280,
                minWidth: 160,
                "& .MuiOutlinedInput-root": {
                  fontSize: "0.75rem",
                  color: theme.palette.brain.table.row.text,
                  pr: search.trim() || appliedSearch ? 0.5 : undefined,
                  backgroundColor: theme.palette.brain.table.tableBackground,
                  "& fieldset": {
                    borderColor: theme.palette.brain.mis.grey.border1,
                  },
                  "&:hover fieldset": {
                    borderColor: theme.palette.secondary.main2,
                  },
                  "&.Mui-focused fieldset": {
                    border: `1px solid ${theme.palette.secondary.main2}`,
                  },
                },
              }}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={handleSearch}
              sx={{
                flexShrink: 0,
                textTransform: "none",
                fontSize: "0.75rem",
                borderColor: theme.palette.secondary.main2,
                color: theme.palette.secondary.main2,
                height: "29px",
                "&:hover": {
                  borderColor: theme.palette.secondary.main2,
                  color: theme.palette.secondary.main2,
                },
              }}
            >
              Search
            </Button>
          </Box>
        )}
      </Box>

      <>
        {/* Filters */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            px: 2,
            py: 1.5,
            borderRadius: 1.5,
            backgroundColor: theme.palette.brain.cyanBlue.background,
            border: `1px solid ${theme.palette.brain.cyanBlue.border}`,
          }}
        >
          {loadingRows ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                flexShrink: 0,
                ml: "auto",
                justifyContent: "end",
                width: "100%",
              }}
            >
              <Skeleton variant="rounded" sx={{ height: "30px", flex: 1 }} />
              <Skeleton
                variant="rounded"
                sx={{ minWidth: "74px", height: "29px" }}
              />
              <Skeleton
                variant="rounded"
                sx={{ minWidth: "64px", height: "29px" }}
              />
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flex: 1,
                  gap: 2,
                  minWidth: 0,
                }}
              >
                {REVIEW_FILTER_FIELDS.map(({ key, label }) => (
                  <FormControl
                    key={key}
                    size="small"
                    sx={{ flex: 1, minWidth: 0, maxWidth: 280 }}
                  >
                    <Select
                      labelId={`review-filter-${key}-label`}
                      displayEmpty
                      value={filters[key] || ""}
                      onChange={(e) =>
                        setFilters((prev) => ({
                          ...prev,
                          [key]: e.target.value,
                        }))
                      }
                      renderValue={(selected) => {
                        if (!selected) {
                          return (
                            <Typography
                              component="span"
                              sx={{
                                fontSize: "0.75rem",
                                color: theme.palette.brain.table.row.text,
                                opacity: 0.65,
                              }}
                            >
                              Select {label}
                            </Typography>
                          );
                        }
                        return (
                          <>
                            <Typography
                              component="span"
                              sx={{
                                color: theme.palette.brain.table.row.text,
                                fontSize: "0.75rem",
                                fontWeight: 700,
                              }}
                            >
                              {label}:
                            </Typography>{" "}
                            <span
                              style={{
                                color: theme.palette.brain.table.row.text,
                              }}
                            >
                              {selected}
                            </span>
                          </>
                        );
                      }}
                      sx={{
                        fontSize: "0.75rem",
                        backgroundColor:
                          theme.palette.brain.table.tableBackground,
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: theme.palette.brain.mis.grey.border1,
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: theme.palette.secondary.main2,
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          border: `1px solid ${theme.palette.secondary.main2}`,
                        },
                      }}
                    >
                      <MenuItem value="" sx={{ fontSize: "0.75rem" }}>
                        <em>Select {label}</em>
                      </MenuItem>
                      {(filterOptions[key] || []).map((option) => (
                        <MenuItem
                          key={option}
                          value={option}
                          sx={{ fontSize: "0.75rem" }}
                        >
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ))}
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexShrink: 0,
                }}
              >
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleApplyFilters}
                  sx={{
                    textTransform: "none",
                    fontSize: "0.75rem",
                    borderColor: theme.palette.secondary.main2,
                    color: theme.palette.secondary.main2,
                    "&:hover": {
                      borderColor: theme.palette.secondary.main2,
                      color: theme.palette.secondary.main2,
                    },
                  }}
                >
                  Apply Filters
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleClearFilters}
                  sx={{
                    textTransform: "none",
                    fontSize: "0.75rem",
                    borderColor: theme.palette.brain.mis.grey.border1,
                    color: theme.palette.primary.text2,
                    "&:hover": {
                      borderColor: theme.palette.brain.mis.grey.border1,
                      color: theme.palette.primary.text2,
                    },
                  }}
                >
                  Clear
                </Button>
              </Box>
            </>
          )}
        </Box>

        {/* Table Section */}
        <Paper
          elevation={0}
          sx={{
            flex: 1,
            minHeight: 0,
            border: `1px solid ${theme.palette.brain.mis.grey.border1}`,
            backgroundColor: theme.palette.brain.table.tableBackground,
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          {loadingRows || loadingColumns ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                position: "relative",
                width: "100%",
                height: "100%",
              }}
            >
              <Loader
                message={"Loading data..."}
                showLoadingImage={false}
                top={"50%"}
                circleStyle={{ color: theme.palette.secondary.main2 }}
              />
            </Box>
          ) : rows?.length === 0 ? (
            <>
              <Box
                sx={{
                  borderRadius: 1.5,
                  py: 6,
                  px: 3,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "text.secondary",
                  bgcolor: theme.palette.brain.table.background1,
                  height: "100%",
                  minHeight: "300px",
                }}
              >
                <InboxOutlined
                  sx={{
                    fontSize: 40,
                    color: theme.palette.secondary.main2,
                    mb: 1,
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.brain.table.header.text,
                  }}
                >
                  No Records found
                </Typography>
              </Box>
            </>
          ) : (
            <TableVirtuoso
              style={{ height: "100%" }}
              data={rows}
              components={virtuosoComponents}
              fixedHeaderContent={() => (
                <TableRow>
                  {tab === "owns" && (
                    <TableCell
                      className={classes.tableHeader}
                      sx={{
                        position: "sticky",
                        left: 0,
                        zIndex: 12,
                        borderRight: `1px solid ${theme.palette.brain.grey.border}`,
                        minWidth: "155px",
                      }}
                    >
                      Active SKU
                    </TableCell>
                  )}

                  {activeColumns.map((col) => {
                    const isFrozen = frozenColumns.has(col.key);
                    const stickyHead = isFrozen
                      ? {
                          position: "sticky",
                          left: frozenLeftOffsets.get(col.key),
                          zIndex: 14,
                          boxShadow: "2px 0 4px -2px rgba(0,0,0,0.08)",
                        }
                      : {};
                    return (
                      <TableCell
                        key={col.key}
                        className={classes.tableHeader}
                        sx={{
                          zIndex: 10,
                          minWidth: APP_CONSTANTS.SMALL_WIDTH_COLUMNS.includes(
                            col.key
                          )
                            ? 50
                            : col.key === "description"
                            ? 550
                            : col.user_edit_access
                            ? 300
                            : 200,
                          ...stickyHead,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            whiteSpace: "nowrap",
                            color: "inherit",
                          }}
                        >
                          <span>{col.label}</span>
                          <Tooltip title={col.info} arrow placement="top">
                            <InfoOutlinedIcon
                              sx={{
                                fontSize: 13,
                                color: theme.palette.brain.grey.text,
                                cursor: "pointer",
                              }}
                            />
                          </Tooltip>
                          {APP_CONSTANTS.FREEZABLE_COLUMNS.includes(
                            col.key
                          ) && (
                            <Box
                              sx={{
                                flex: 1,
                                display: "flex",
                                justifyContent: "end",
                              }}
                            >
                              <Tooltip
                                title={
                                  isFrozen ? "Unfreeze column" : "Freeze column"
                                }
                                arrow
                              >
                                <IconButton
                                  size="small"
                                  onClick={() => toggleFreeze(col.key)}
                                  sx={{ p: 0.25 }}
                                >
                                  {isFrozen ? (
                                    <PushPinIcon
                                      sx={{
                                        fontSize: 14,
                                        color:
                                          theme.palette.brain.table.header
                                            .restrictedText,
                                      }}
                                    />
                                  ) : (
                                    <PushPinOutlinedIcon
                                      sx={{
                                        fontSize: 14,
                                        color: theme.palette.brain.grey.text,
                                      }}
                                    />
                                  )}
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                    );
                  })}
                </TableRow>
              )}
              itemContent={(idx, row) => (
                <MemoizedRowCells
                  row={row}
                  idx={idx}
                  columns={activeColumns}
                  theme={theme}
                  classes={classes}
                  frozenColumns={frozenColumns}
                  rowBg={() => {
                    return idx % 2 === 0
                      ? theme.palette.brain.drawer.item_background
                      : theme.palette.brain.table.row.background;
                  }}
                  frozenLeftOffsets={frozenLeftOffsets}
                  skuCount={skuRecords?.[row.upc12]?.length || 0}
                  skuList={(skuRecords?.[row.upc12] || []).map(
                    (item, _) => item.sku
                  )}
                  handleSkuClick={openSkuForUpc}
                  tab={tab}
                />
              )}
            />
          )}
        </Paper>

        {/* Pagination */}
        {pagination && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: 2,
              py: 1,
              borderRadius: 1.5,
              backgroundColor: theme.palette.brain.cyanBlue.background,
              border: `1px solid ${theme.palette.brain.cyanBlue.border}`,
            }}
          >
            {loadingRows ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  flexShrink: 0,
                  ml: "auto",
                  justifyContent: "end",
                  width: "100%",
                }}
              >
                <Skeleton variant="rounded" sx={{ height: "30px", flex: 1 }} />
                <Skeleton
                  variant="rounded"
                  sx={{ minWidth: "74px", height: "29px" }}
                />
                <Skeleton
                  variant="rounded"
                  sx={{ minWidth: "64px", height: "29px" }}
                />
              </Box>
            ) : (
              <>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "0.72rem",
                    color: theme.palette.primary.text2,
                  }}
                >
                  {pagination
                    ? `Showing ${
                        (page - 1) * selectedPageSize + 1
                      } – ${Math.min(
                        page * selectedPageSize,
                        pagination.total_records
                      )} of ${pagination.total_records}`
                    : ""}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "0.75rem",
                      color: theme.palette.primary.text2,
                      fontWeight: 500,
                    }}
                  >
                    Page Size:
                  </Typography>
                  <DisplayPageSize
                    theme={theme}
                    selectedPageSize={selectedPageSize}
                    handlePageSizeChange={handlePageSizeChange}
                  />
                  <IconButton
                    size="small"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1 || loadingRows}
                    sx={{ color: theme.palette.secondary.main2 }}
                  >
                    <NavigateBeforeIcon />
                  </IconButton>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "0.72rem",
                      color: theme.palette.primary.text2,
                    }}
                  >
                    Page {page} of {pagination?.total_pages}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() =>
                      setPage((p) => Math.min(pagination?.total_pages, p + 1))
                    }
                    disabled={page === pagination?.total_pages || loadingRows}
                    sx={{ color: theme.palette.secondary.main2 }}
                  >
                    <NavigateNextIcon />
                  </IconButton>
                </Box>
              </>
            )}
          </Box>
        )}
      </>

      {/* SKU Drawer */}
      {skuDrawer?.open && (
        <SKUDetailsDrawer
          open={skuDrawer?.open}
          data={skuDrawer.data}
          onClose={() => {
            setSkuDrawer({ open: false, data: {} });
          }}
        />
      )}
    </Box>
  );
}
